import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Button,
  TextField,
  Typography,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  AddRounded,
  EditRounded,
  DeleteOutlineRounded,
  LinkRounded,
  LinkOffRounded,
  SensorsRounded,
  OpenInNewRounded,
  ViewCarouselRounded,
  ContentCopyRounded,
  LayersRounded,
  SaveRounded,
  PlaylistAddRounded,
  CheckRounded,
  BoltRounded,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { errorMessage } from '../lib/api';
import { relativeTime } from '../lib/format';
import { palette } from '../theme';
import { GlassPanel, SectionTitle, StatusPill, PanelLoader } from '../components/ui/GlassPanel';
import DataTable from '../components/data/DataTable';
import { useToast } from '../components/ui/ToastProvider';
import { useAuth } from '../App';

const DEVICE_TYPES = [
  'standard-node',
  'environment-sensor',
  'energy-meter',
  'smart-signage',
  'gps-tracker',
  'industrial-gateway',
  'access-controller',
];

const PROTOCOLS = [
  { value: 'mqtt', label: 'MQTT' },
  { value: 'tcp', label: 'TCP' },
  { value: 'websocket', label: 'WebSocket' },
  { value: 'rest', label: 'REST' },
];

const TEMPLATE_STORAGE_KEY = 'bluenode.device.templates';

const EMPTY_FORM = {
  device_id: '',
  device_name: '',
  serial_number: '',
  device_type: 'standard-node',
  protocol: 'mqtt',
  firmware_version: '1.0.0',
  assigned_user: '',
  status: 'offline',
  location: '',
  signage_required: false,
  operation_buttons: [],
};

/**
 * Catalogue of common remote operations an administrator can enable on an
 * endpoint. They are offered as one-click presets; custom codes stay possible.
 */
export const REMOTE_ACTION_PRESETS = [
  { label: 'Relay ON', code: 'RELAY_ON' },
  { label: 'Relay OFF', code: 'RELAY_OFF' },
  { label: 'Restart', code: 'RESTART' },
  { label: 'Refresh', code: 'REFRESH' },
  { label: 'Sync Time', code: 'SYNC_TIME' },
  { label: 'Factory Reset', code: 'FACTORY_RESET' },
  { label: 'Self Test', code: 'SELF_TEST' },
  { label: 'Read Telemetry', code: 'READ_TELEMETRY' },
];

const readTemplates = () => {
  try {
    return JSON.parse(localStorage.getItem(TEMPLATE_STORAGE_KEY) || '[]');
  } catch (error) {
    return [];
  }
};

export default function Devices() {
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isAdmin = user?.role === 'admin';

  const [devices, setDevices] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, mode: 'create', target: null });
  const [createMode, setCreateMode] = useState('single');
  const [bulkIds, setBulkIds] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignUser, setAssignUser] = useState('');
  const [templates, setTemplates] = useState(readTemplates);
  const [created, setCreated] = useState(null);
  const [tableReset, setTableReset] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/devices/', { params: { limit: 500 } });
      setDevices(response.data || []);
    } catch (error) {
      toast.error(errorMessage(error, 'Unable to load the device fleet.'));
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!isAdmin) return;
    api
      .get('/admin/users/directory')
      .then((response) => setOperators(response.data || []))
      .catch(() => setOperators([]));
  }, [isAdmin]);

  const bulkIdList = useMemo(
    () =>
      bulkIds
        .split('\n')
        .map((id) => id.trim())
        .filter(Boolean),
    [bulkIds]
  );

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setBulkIds('');
    setCreateMode('single');
    setCreated(null);
    setDialog({ open: true, mode: 'create', target: null });
  };

  const openEdit = (device) => {
    const properties = device.properties || {};
    setForm({
      device_id: device.device_id,
      device_name: properties.device_name || device.device_id,
      serial_number: properties.serial_number || '',
      device_type: device.device_type,
      protocol: device.protocol || 'mqtt',
      firmware_version: device.firmware_version || '1.0.0',
      assigned_user: device.user_id ? String(device.user_id) : '',
      status: device.status,
      location: device.location || '',
      signage_required: Boolean(device.signage_required),
      operation_buttons: (device.operation_buttons || []).map((button) => ({
        label: button.label || '',
        code: button.code || '',
      })),
    });
    setErrors({});
    setCreated(null);
    setDialog({ open: true, mode: 'edit', target: device });
  };

  const validate = () => {
    const next = {};
    if (dialog.mode === 'create' && createMode === 'single') {
      if (!form.device_id.trim()) next.device_id = 'Device ID is required';
      if (!form.device_name.trim()) next.device_name = 'Device name is required';
      if (!form.serial_number.trim()) next.serial_number = 'Serial number is required';
    }
    if (dialog.mode === 'create' && createMode === 'bulk') {
      if (bulkIdList.length === 0) next.bulk_ids = 'Enter at least one device ID (one per line)';
    }
    if (!form.device_type) next.device_type = 'Device type is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildPayload = (overrides = {}) => ({
    device_type: form.device_type,
    protocol: form.protocol,
    firmware_version: form.firmware_version.trim() || '1.0.0',
    location: form.location.trim() || null,
    signage_required: form.signage_required,
    operation_buttons: form.operation_buttons
      .filter((button) => button.label.trim() && button.code.trim())
      .map((button) => ({
        label: button.label.trim(),
        code: button.code.trim().toUpperCase().replace(/\s+/g, '_'),
      })),
    ...overrides,
  });

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);

    try {
      if (dialog.mode === 'create' && createMode === 'bulk') {
        const response = await api.post('/devices/bulk', {
          ...buildPayload(),
          device_ids: bulkIdList,
          properties: { serial_number: form.serial_number.trim() || null },
          user_id: isAdmin && form.assigned_user ? Number(form.assigned_user) : null,
        });
        const result = response.data || {};
        setCreated({
          bulk: true,
          requested: result.requested,
          created: result.created,
          failed: result.failed,
          devices: result.devices || [],
          failures: result.failures || [],
        });
        if (result.created) {
          toast.success(`${result.created} of ${result.requested} devices registered.`);
        }
        if (result.failed) {
          toast.warning(`${result.failed} identifier(s) could not be registered.`);
        }
        load();
        setTableReset((current) => current + 1);
        return;
      }

      if (dialog.mode === 'create') {
        const response = await api.post('/devices/', {
          ...buildPayload(),
          device_id: form.device_id.trim(),
          properties: {
            device_name: form.device_name.trim(),
            serial_number: form.serial_number.trim(),
          },
          user_id: isAdmin && form.assigned_user ? Number(form.assigned_user) : null,
          status: form.status,
        });
        setCreated({ bulk: false, devices: [response.data] });
        toast.success(`Device ${form.device_id.trim()} registered.`);
        setTableReset((current) => current + 1);
      } else {
        await api.patch(`/devices/${dialog.target.device_id}`, {
          device_type: form.device_type,
          protocol: form.protocol,
          firmware_version: form.firmware_version.trim() || '1.0.0',
          location: form.location.trim() || null,
          signage_required: form.signage_required,
          properties: {
            device_name: form.device_name.trim(),
            serial_number: form.serial_number.trim(),
          },
        });
        if (form.assigned_user && String(form.assigned_user) !== String(dialog.target.user_id || '')) {
          await api.post(`/admin/devices/${dialog.target.device_id}/assign`, {
            user_id: Number(form.assigned_user),
          });
        }
        toast.success(`Device ${form.device_id.trim()} updated.`);
        setDialog({ open: false, mode: 'create', target: null });
      }
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'The device could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  const runOperation = async (device, action) => {
    try {
      await api.post(`/devices/${device.device_id}/command`, {
        action: action.code,
        label: action.label,
      });
      toast.success(`"${action.label}" dispatched to ${device.device_id}.`);
    } catch (error) {
      toast.error(errorMessage(error, 'The remote operation could not be dispatched.'));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/devices/${deleteTarget.device_id}`);
      toast.success(`Device ${deleteTarget.device_id} removed.`);
      setDeleteTarget(null);
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'The device could not be deleted.'));
    }
  };

  const handleAssign = async () => {
    if (!assignTarget) return;
    try {
      if (assignUser) {
        await api.post(`/admin/devices/${assignTarget.device_id}/assign`, { user_id: Number(assignUser) });
        toast.success(`Device ${assignTarget.device_id} assigned.`);
      } else {
        await api.delete(`/admin/devices/${assignTarget.device_id}/assign`);
        toast.info(`Device ${assignTarget.device_id} unassigned.`);
      }
      setAssignTarget(null);
      setAssignUser('');
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'The assignment could not be updated.'));
    }
  };

  const saveTemplate = () => {
    const name = window.prompt('Template name');
    if (!name) return;
    const next = [...templates, { id: Date.now(), name, payload: form }];
    setTemplates(next);
    localStorage.setItem(TEMPLATE_STORAGE_KEY, JSON.stringify(next));
    toast.success(`Template "${name}" saved.`);
  };

  const applyTemplate = (templateId) => {
    const template = templates.find((item) => String(item.id) === String(templateId));
    if (!template) return;
    setForm({ ...EMPTY_FORM, ...template.payload });
    toast.info(`Template "${template.name}" applied.`);
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    }
    toast.success('Copied to clipboard.');
  };

  const columns = useMemo(
    () => [
      {
        key: 'device_id',
        label: 'Device ID',
        width: 170,
        render: (row) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: 1.5,
                display: 'grid',
                placeItems: 'center',
                background: alpha(row.signage_required ? palette.secondary : palette.primary, 0.13),
                border: `1px solid ${alpha(row.signage_required ? palette.secondary : palette.primary, 0.3)}`,
              }}
            >
              {row.signage_required ? (
                <ViewCarouselRounded sx={{ fontSize: 14, color: palette.secondary }} />
              ) : (
                <SensorsRounded sx={{ fontSize: 14, color: palette.primary }} />
              )}
            </Box>
            <Typography className="mono" sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
              {row.device_id}
            </Typography>
          </Box>
        ),
      },
      {
        key: 'device_name',
        label: 'Device Name',
        render: (row) => (
          <Box>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
              {(row.properties || {}).device_name || row.device_id}
            </Typography>
            <Typography className="mono" sx={{ fontSize: '0.66rem', color: 'text.secondary' }}>
              {(row.properties || {}).serial_number || (isAdmin ? row.device_type : '—')}
            </Typography>
          </Box>
        ),
        sortValue: (row) => (row.properties || {}).device_name || row.device_id,
      },
      {
        key: 'owner_username',
        label: 'User',
        render: (row) =>
          row.owner_username ? (
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{row.owner_username}</Typography>
              {row.owner_full_name && row.owner_full_name !== row.owner_username && (
                <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary' }}>{row.owner_full_name}</Typography>
              )}
            </Box>
          ) : (
            <Chip
              size="small"
              label="Unassigned"
              sx={{ fontSize: '0.66rem', color: 'text.secondary', background: alpha('#334155', 0.3) }}
            />
          ),
      },
      {
        key: 'status',
        label: 'Status',
        render: (row) => (
          <StatusPill
            label={row.status}
            tone={row.status === 'online' ? 'success' : row.status === 'updating' ? 'warning' : 'danger'}
          />
        ),
      },
      {
        key: 'last_seen',
        label: 'Last Seen',
        render: (row) => (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{relativeTime(row.last_seen)}</Typography>
        ),
      },
      {
        key: 'firmware_version',
        label: 'Firmware',
        render: (row) => (
          <Typography className="mono" sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>
            v{row.firmware_version}
          </Typography>
        ),
      },
      {
        key: 'operations',
        label: 'Operations',
        sortable: false,
        render: (row) => {
          const actions = (row.operation_buttons || []).filter((button) => button.code);
          if (!actions.length) {
            return (
              <Typography sx={{ fontSize: '0.72rem', color: alpha(palette.secondaryText, 0.7) }}>
                No actions
              </Typography>
            );
          }
          return (
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {actions.map((button) => (
                <Button
                  key={button.code}
                  size="small"
                  variant="outlined"
                  startIcon={<BoltRounded sx={{ fontSize: 14 }} />}
                  onClick={() => runOperation(row, button)}
                  sx={{ fontSize: '0.7rem', py: 0.4, borderColor: alpha(palette.secondary, 0.4), color: palette.secondary }}
                >
                  {button.label}
                </Button>
              ))}
            </Box>
          );
        },
      },
      {
        key: 'actions',
        label: 'Actions',
        sortable: false,
        align: 'right',
        render: (row) => (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
            <Tooltip title="Open device profile">
              <IconButton size="small" onClick={() => navigate(`/devices/${row.device_id}`)} sx={{ color: palette.primary }}>
                <OpenInNewRounded sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            {isAdmin && (
              <>
                <Tooltip title="Edit device">
                  <IconButton size="small" onClick={() => openEdit(row)} sx={{ color: 'text.secondary' }}>
                    <EditRounded sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={row.user_id ? 'Reassign device' : 'Assign device'}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setAssignTarget(row);
                      setAssignUser(row.user_id ? String(row.user_id) : '');
                    }}
                    sx={{ color: palette.secondary }}
                  >
                    {row.user_id ? <LinkRounded sx={{ fontSize: 16 }} /> : <LinkOffRounded sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete device">
                  <IconButton size="small" onClick={() => setDeleteTarget(row)} sx={{ color: palette.danger }}>
                    <DeleteOutlineRounded sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAdmin, navigate]
  );

  const prefillSearch = searchParams.get('search') || '';
  const isBulk = dialog.mode === 'create' && createMode === 'bulk';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <GlassPanel sx={{ p: 3 }}>
        <SectionTitle
          title={isAdmin ? 'Device Fleet' : 'My Devices'}
          subtitle={
            isAdmin
              ? 'Register, assign and monitor every endpoint on the platform'
              : 'Endpoints currently assigned to your operator account'
          }
          icon={<SensorsRounded sx={{ fontSize: 18 }} />}
          action={
            isAdmin ? (
              <Button variant="contained" startIcon={<AddRounded />} onClick={openCreate}>
                Add Device
              </Button>
            ) : null
          }
        />
      </GlassPanel>

      <GlassPanel>
        {loading ? (
          <PanelLoader label="Loading device fleet" />
        ) : (
          <DataTable
            columns={columns}
            rows={devices}
            rowKey={(row) => row.device_id}
            searchKeys={[
              'device_id',
              'device_type',
              'owner_username',
              (row) => (row.properties || {}).device_name,
              (row) => (row.properties || {}).serial_number,
            ]}
            searchPlaceholder={prefillSearch || 'Search devices…'}
            initialSearch={prefillSearch}
            resetKey={tableReset}
            filters={[
              {
                key: 'status',
                label: 'Status',
                options: [
                  { value: 'online', label: 'Online' },
                  { value: 'offline', label: 'Offline' },
                  { value: 'updating', label: 'Updating' },
                ],
              },
              ...(isAdmin
                ? [
                    {
                      key: 'device_type',
                      label: 'Type',
                      options: DEVICE_TYPES.map((type) => ({ value: type, label: type })),
                    },
                  ]
                : []),
            ]}
            emptyIcon={<SensorsRounded />}
            emptyTitle="No devices registered"
            emptyDescription={
              isAdmin
                ? 'Register the first endpoint to begin monitoring live telemetry.'
                : 'Register an endpoint to begin monitoring live telemetry.'
            }
            pageSize={10}
          />
        )}
      </GlassPanel>

      {/* create / edit dialog */}
      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, mode: 'create', target: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {dialog.mode === 'create' ? 'Register Device' : `Edit ${dialog.target?.device_id}`}
        </DialogTitle>
        <DialogContent>
          {dialog.mode === 'create' && (
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={createMode}
              onChange={(_, value) => value && setCreateMode(value)}
              sx={{ mb: 2.5 }}
            >
              <ToggleButton value="single" sx={{ gap: 1 }}>
                <SensorsRounded sx={{ fontSize: 16 }} /> Single
              </ToggleButton>
              <ToggleButton value="bulk" sx={{ gap: 1 }}>
                <PlaylistAddRounded sx={{ fontSize: 16 }} /> Bulk create
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          <Grid container spacing={2}>
            {isBulk ? (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  label="Device IDs"
                  placeholder={'NODE-001\nNODE-002\nNODE-003'}
                  value={bulkIds}
                  onChange={(event) => {
                    setBulkIds(event.target.value);
                    if (errors.bulk_ids) setErrors((current) => ({ ...current, bulk_ids: undefined }));
                  }}
                  error={Boolean(errors.bulk_ids)}
                  helperText={
                    errors.bulk_ids ||
                    `One identifier per line — ${bulkIdList.length} device${bulkIdList.length === 1 ? '' : 's'} will be registered.`
                  }
                  sx={{ '& .MuiOutlinedInput-root': { fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8rem' } }}
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Device ID"
                    value={form.device_id}
                    disabled={dialog.mode === 'edit'}
                    onChange={(event) => setForm({ ...form, device_id: event.target.value })}
                    error={Boolean(errors.device_id)}
                    helperText={errors.device_id}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Device name"
                    value={form.device_name}
                    onChange={(event) => setForm({ ...form, device_name: event.target.value })}
                    error={Boolean(errors.device_name)}
                    helperText={errors.device_name}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Serial number"
                    value={form.serial_number}
                    onChange={(event) => setForm({ ...form, serial_number: event.target.value })}
                    error={Boolean(errors.serial_number)}
                    helperText={errors.serial_number}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" error={Boolean(errors.device_type)}>
                <InputLabel>Device type</InputLabel>
                <Select
                  label="Device type"
                  value={form.device_type}
                  onChange={(event) => setForm({ ...form, device_type: event.target.value })}
                >
                  {DEVICE_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Protocol</InputLabel>
                <Select
                  label="Protocol"
                  value={form.protocol}
                  onChange={(event) => setForm({ ...form, protocol: event.target.value })}
                >
                  {PROTOCOLS.map((protocol) => (
                    <MenuItem key={protocol.value} value={protocol.value}>
                      {protocol.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Firmware version"
                value={form.firmware_version}
                onChange={(event) => setForm({ ...form, firmware_version: event.target.value })}
              />
            </Grid>

            {dialog.mode === 'create' && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={form.status}
                    onChange={(event) => setForm({ ...form, status: event.target.value })}
                  >
                    <MenuItem value="offline">Offline</MenuItem>
                    <MenuItem value="online">Online</MenuItem>
                    <MenuItem value="updating">Updating</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            {isAdmin && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Assigned operator</InputLabel>
                  <Select
                    label="Assigned operator"
                    value={form.assigned_user}
                    onChange={(event) => setForm({ ...form, assigned_user: event.target.value })}
                  >
                    <MenuItem value="">Unassigned</MenuItem>
                    {operators.map((operator) => (
                      <MenuItem key={operator.id} value={String(operator.id)}>
                        {operator.username} — {operator.full_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Location"
                value={form.location}
                onChange={(event) => setForm({ ...form, location: event.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                onClick={() => setForm({ ...form, signage_required: !form.signage_required })}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  p: 1.4,
                  borderRadius: 2.5,
                  cursor: 'pointer',
                  border: `1px solid ${alpha(form.signage_required ? palette.secondary : palette.primary, 0.25)}`,
                  background: alpha(form.signage_required ? palette.secondary : palette.primary, 0.07),
                }}
              >
                <ViewCarouselRounded sx={{ color: palette.secondary, fontSize: 20 }} />
                <Box>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Digital signage endpoint</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    Enables the dedicated signage visualisation for this device only.
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Chip
                  size="small"
                  label={form.signage_required ? 'Enabled' : 'Disabled'}
                  sx={{
                    color: form.signage_required ? palette.secondary : 'text.secondary',
                    background: alpha(form.signage_required ? palette.secondary : '#334155', 0.18),
                    fontSize: '0.66rem',
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  p: 1.75,
                  borderRadius: 3,
                  border: `1px solid ${alpha(palette.secondary, 0.18)}`,
                  background: alpha(palette.secondary, 0.05),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <BoltRounded sx={{ fontSize: 17, color: palette.secondary }} />
                  <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, letterSpacing: '0.1em', color: 'text.secondary' }}>
                    OPERATION ACTIONS
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddRounded sx={{ fontSize: 14 }} />}
                    onClick={() =>
                      setForm((current) => ({
                        ...current,
                        operation_buttons: [...current.operation_buttons, { label: '', code: '' }],
                      }))
                    }
                  >
                    Add action
                  </Button>
                </Box>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1.5, lineHeight: 1.5 }}>
                  These remote operations become the action buttons available to the operator
                  assigned to this device.
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.75 }}>
                  {REMOTE_ACTION_PRESETS.map((preset) => {
                    const enabled = form.operation_buttons.some(
                      (item) => item.code.trim().toUpperCase() === preset.code
                    );
                    return (
                      <Chip
                        key={preset.code}
                        size="small"
                        label={preset.label}
                        icon={enabled ? <CheckRounded sx={{ fontSize: 13 }} /> : <AddRounded sx={{ fontSize: 13 }} />}
                        onClick={() =>
                          setForm((current) => ({
                            ...current,
                            operation_buttons: enabled
                              ? current.operation_buttons.filter(
                                  (item) => item.code.trim().toUpperCase() !== preset.code
                                )
                              : [...current.operation_buttons, { label: preset.label, code: preset.code }],
                          }))
                        }
                        sx={{
                          cursor: 'pointer',
                          fontSize: '0.7rem',
                          color: enabled ? palette.secondary : 'text.secondary',
                          background: enabled ? alpha(palette.secondary, 0.16) : alpha('#334155', 0.24),
                          border: `1px solid ${alpha(enabled ? palette.secondary : palette.primary, 0.28)}`,
                          '& .MuiChip-icon': { color: 'inherit', fontSize: 13 },
                        }}
                      />
                    );
                  })}
                </Box>

                {form.operation_buttons.length === 0 && (
                  <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary', fontStyle: 'italic' }}>
                    No actions defined - the operator console will not show operation controls.
                  </Typography>
                )}

                <Box sx={{ display: 'grid', gap: 1 }}>
                  {form.operation_buttons.map((button, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        size="small"
                        label="Action label"
                        placeholder="Reboot"
                        value={button.label}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            operation_buttons: current.operation_buttons.map((item, itemIndex) =>
                              itemIndex === index ? { ...item, label: event.target.value } : item
                            ),
                          }))
                        }
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        size="small"
                        label="Action code"
                        placeholder="REBOOT"
                        value={button.code}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            operation_buttons: current.operation_buttons.map((item, itemIndex) =>
                              itemIndex === index ? { ...item, code: event.target.value } : item
                            ),
                          }))
                        }
                        sx={{ flex: 1 }}
                      />
                      <Tooltip title="Remove action">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setForm((current) => ({
                              ...current,
                              operation_buttons: current.operation_buttons.filter((_, itemIndex) => itemIndex !== index),
                            }))
                          }
                          sx={{ color: palette.danger }}
                        >
                          <DeleteOutlineRounded sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>

            {dialog.mode === 'create' && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    p: 1.25,
                    borderRadius: 2.5,
                    border: `1px solid ${alpha(palette.primary, 0.12)}`,
                    background: alpha(palette.primary, 0.04),
                    flexWrap: 'wrap',
                  }}
                >
                  <LayersRounded sx={{ fontSize: 17, color: palette.primary }} />
                  <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary', flex: 1, minWidth: 140 }}>
                    Provisioning template
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 170 }}>
                    <Select value="" displayEmpty onChange={(event) => applyTemplate(event.target.value)}>
                      <MenuItem value="" disabled>
                        Apply template…
                      </MenuItem>
                      {templates.map((template) => (
                        <MenuItem key={template.id} value={String(template.id)}>
                          {template.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button size="small" variant="outlined" startIcon={<SaveRounded sx={{ fontSize: 14 }} />} onClick={saveTemplate}>
                    Save as template
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>

          {/* creation result */}
          {created && (
            <Box
              sx={{
                mt: 2.5,
                p: 2,
                borderRadius: 3,
                border: `1px solid ${alpha(palette.success, 0.3)}`,
                background: alpha(palette.success, 0.06),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckRounded sx={{ color: palette.success, fontSize: 18 }} />
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>
                  {created.bulk
                    ? `${created.created} of ${created.requested} devices registered`
                    : 'Device registered successfully'}
                </Typography>
              </Box>

              <Box sx={{ mt: 1.5, display: 'grid', gap: 1 }}>
                {created.devices.map((device) => (
                  <Box
                    key={device.device_id}
                    sx={{
                      p: 1.25,
                      borderRadius: 2.5,
                      background: alpha('#050816', 0.5),
                      border: `1px solid ${alpha(palette.primary, 0.12)}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography className="mono" sx={{ fontSize: '0.76rem', fontWeight: 600, flex: 1 }}>
                        {device.device_id}
                      </Typography>
                      {device.device_token && (
                        <Tooltip title="Copy device token">
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(device.device_token)}
                            sx={{ color: 'text.secondary' }}
                          >
                            <ContentCopyRounded sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    {device.rest_endpoints?.latest_data && (
                      <Typography className="mono" sx={{ fontSize: '0.64rem', color: 'text.secondary', mt: 0.5, wordBreak: 'break-all' }}>
                        {device.rest_endpoints.latest_data}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>

              {created.failures?.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  {created.failures.map((failure) => (
                    <Box key={failure.device_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4 }}>
                      <Chip size="small" label={failure.device_id} sx={{ fontSize: '0.64rem', background: alpha(palette.danger, 0.14), color: palette.danger }} />
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{failure.error}</Typography>
                    </Box>
                  ))}
                </Box>
              )}

              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setDialog({ open: false, mode: 'create', target: null });
                  setCreated(null);
                }}
                sx={{ mt: 1.75 }}
              >
                Close
              </Button>
            </Box>
          )}
        </DialogContent>
        <Divider sx={{ borderColor: alpha(palette.primary, 0.1) }} />
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDialog({ open: false, mode: 'create', target: null })} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={isBulk ? <PlaylistAddRounded sx={{ fontSize: 16 }} /> : <AddRounded sx={{ fontSize: 16 }} />}
          >
            {saving
              ? 'Saving…'
              : isBulk
                ? `Create ${bulkIdList.length} Device${bulkIdList.length === 1 ? '' : 's'}`
                : dialog.mode === 'create'
                  ? 'Register device'
                  : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* delete confirmation */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete device</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', lineHeight: 1.6 }}>
            Permanently remove{' '}
            <Box component="span" className="mono" sx={{ color: 'text.primary' }}>
              {deleteTarget?.device_id}
            </Box>{' '}
            and its telemetry history from the platform?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete device
          </Button>
        </DialogActions>
      </Dialog>

      {/* assignment dialog */}
      <Dialog open={Boolean(assignTarget)} onClose={() => setAssignTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Assign device</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', mb: 2 }}>
            Choose the operator accountable for{' '}
            <Box component="span" className="mono" sx={{ color: 'text.primary' }}>
              {assignTarget?.device_id}
            </Box>
            . Leave unassigned to release the endpoint.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Operator</InputLabel>
            <Select label="Operator" value={assignUser} onChange={(event) => setAssignUser(event.target.value)}>
              <MenuItem value="">Unassigned</MenuItem>
              {operators.map((operator) => (
                <MenuItem key={operator.id} value={String(operator.id)}>
                  {operator.username} — {operator.full_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setAssignTarget(null)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAssign}>
            Apply assignment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
